import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { BRAND, WHATSAPP_URL } from "@/data/site";
import { lenisRef } from "@/lib/lenis";
import { Logo } from "@/components/site/Logo";
import { useI18n } from "@/i18n";

const LINKS = [
  { k: "nav.solutions", href: "#solutii" },
  { k: "nav.services", href: "#servicii" },
  { k: "nav.monitor", href: "#monitorizare" },
  { k: "nav.status", href: "#status" },
  { k: "nav.cases", href: "#studii" },
  { k: "nav.team", href: "#echipa" },
  { k: "nav.contact", href: "#contact" },
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { lang, setLang, t } = useI18n();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (href) => {
    setOpen(false);
    if (location.pathname !== "/") {
      navigate(`/${href}`); // ex: /#contact — Home derulează la hash după montare
      return;
    }
    const lenis = lenisRef.current;
    if (lenis) lenis.scrollTo(href, { offset: -60, duration: 1.2 });
    else document.querySelector(href)?.scrollIntoView({ behavior: "smooth" });
  };

  const goBlog = () => {
    setOpen(false);
    navigate("/blog");
  };

  const goHome = () => {
    setOpen(false);
    if (location.pathname !== "/") navigate("/");
    else {
      const lenis = lenisRef.current;
      if (lenis) lenis.scrollTo(0, { duration: 1.0 });
      else window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  return (
    <motion.header
      data-testid="navbar"
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1], delay: 0.2 }}
      className={`fixed inset-x-0 top-0 z-50 transition-colors duration-500 ${
        scrolled
          ? "bg-black/70 backdrop-blur-xl border-b border-white/10"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <nav className="mx-auto flex max-w-7xl items-center justify-between px-5 py-4 sm:px-8">
        <button
          data-testid="nav-logo"
          onClick={goHome}
          className="group flex items-center gap-2.5"
        >
          <span className="relative grid place-items-center transition-transform duration-300 group-hover:scale-105">
            <Logo size={34} />
          </span>
          <span className="font-display text-base font-bold tracking-tight text-white">
            {BRAND.short}
            <span className="text-cyan-neon">.</span>
            <span className="ml-1 font-mono text-[9px] font-normal uppercase tracking-[0.2em] text-white/40">
              Technology
            </span>
          </span>
        </button>

        <div className="hidden items-center gap-8 md:flex">
          {LINKS.map((l) => (
            <button
              key={l.href}
              data-testid={`nav-link-${l.href.slice(1)}`}
              onClick={() => go(l.href)}
              className="font-mono text-xs uppercase tracking-[0.15em] text-white/60 transition-colors duration-300 hover:text-cyan-neon"
            >
              {t(l.k)}
            </button>
          ))}
          <button
            data-testid="nav-link-blog"
            onClick={goBlog}
            className="font-mono text-xs uppercase tracking-[0.15em] text-white/60 transition-colors duration-300 hover:text-cyan-neon"
          >
            {t("nav.blog")}
          </button>
          <button
            data-testid="lang-toggle"
            onClick={() => setLang(lang === "ro" ? "en" : "ro")}
            className="flex items-center gap-1 font-mono text-xs font-bold uppercase tracking-[0.1em]"
          >
            <span className={lang === "ro" ? "text-cyan-neon" : "text-white/40"}>RO</span>
            <span className="text-white/20">/</span>
            <span className={lang === "en" ? "text-cyan-neon" : "text-white/40"}>EN</span>
          </button>
          <a
            data-testid="nav-cta"
            href={WHATSAPP_URL}
            target="_blank"
            rel="noreferrer"
            className="rounded-none border border-cyan-neon bg-cyan-neon px-5 py-2.5 font-mono text-xs font-bold uppercase tracking-[0.12em] text-black transition-colors duration-300 hover:bg-white hover:border-white"
          >
            {t("nav.cta")}
          </a>
        </div>

        <button
          data-testid="nav-menu-toggle"
          onClick={() => setOpen((v) => !v)}
          className="grid h-10 w-10 place-items-center rounded-md border border-white/15 text-white md:hidden"
          aria-label="Meniu"
        >
          {open ? <X size={18} /> : <Menu size={18} />}
        </button>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="overflow-hidden border-t border-white/10 bg-black/95 backdrop-blur-xl md:hidden"
          >
            <div className="flex flex-col gap-1 px-5 py-4">
              {LINKS.map((l) => (
                <button
                  key={l.href}
                  data-testid={`nav-mobile-link-${l.href.slice(1)}`}
                  onClick={() => go(l.href)}
                  className="py-3 text-left font-display text-lg font-semibold text-white/80"
                >
                  {t(l.k)}
                </button>
              ))}
              <button
                data-testid="nav-mobile-link-blog"
                onClick={goBlog}
                className="py-3 text-left font-display text-lg font-semibold text-white/80"
              >
                {t("nav.blog")}
              </button>
              <button
                data-testid="lang-toggle-mobile"
                onClick={() => setLang(lang === "ro" ? "en" : "ro")}
                className="flex items-center gap-1 py-3 text-left font-mono text-sm font-bold uppercase tracking-wide"
              >
                <span className={lang === "ro" ? "text-cyan-neon" : "text-white/40"}>RO</span>
                <span className="text-white/20">/</span>
                <span className={lang === "en" ? "text-cyan-neon" : "text-white/40"}>EN</span>
              </button>
              <a
                data-testid="nav-mobile-cta"
                href={WHATSAPP_URL}
                target="_blank"
                rel="noreferrer"
                className="mt-2 bg-cyan-neon px-5 py-3 text-center font-mono text-sm font-bold uppercase tracking-wide text-black"
              >
                {t("nav.cta")}
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
